"use client";

import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import Link from "next/link";
import { useAuthForms } from "@/lib/hooks/auth/use-auth-forms";
import { GoogleSignInButton } from "@wtc/ui";
import { AppleSignInButton } from "@/components/auth/AppleSignInButton";
import { PasskeySignInButton } from "@/components/auth/PasskeySignInButton";
import { isAppleSignInEnabled } from "@/lib/feature-flags";

interface AuthFormProps {
  userType: "employee" | "client";
  title: string;
  description: string;
  signUpTitle: string;
  signUpDescription: string;
  helpText: string;
  /** Safe app-relative path (e.g. after OAuth consent) — see `isSafeHubRelativePath` */
  redirectAfterLogin?: string | null;
}

export function AuthForm({
  userType,
  title,
  description,
  signUpTitle,
  signUpDescription,
  helpText,
  redirectAfterLogin,
}: AuthFormProps) {
  const {
    email,
    password,
    firstName,
    lastName,
    isSignUp,
    isLoading,
    error,
    success,
    setEmail,
    setPassword,
    setFirstName,
    setLastName,
    setIsSignUp,
    setError,
    setSuccess,
    handleSignIn,
    handleSignUp,
    handleMagicLink,
    handleGoogleSignIn,
    handleAppleSignIn,
    handlePasskeySignIn,
  } = useAuthForms(redirectAfterLogin);

  return (
    <div className="min-h-screen flex items-center justify-center bg-card-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <Image
              src="/logo_long.svg"
              alt="West Tennessee Consulting Hub"
              width={200}
              height={48}
              className="h-12 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            {isSignUp ? signUpTitle : title}
          </CardTitle>
          <CardDescription className="text-center">
            {isSignUp ? signUpDescription : description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={isSignUp ? handleSignUp : (e) => handleSignIn(e, userType)}
            className="space-y-4"
          >
            {isSignUp && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="Enter your first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required={isSignUp}
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Enter your last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required={isSignUp}
                    disabled={isLoading}
                  />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder={isSignUp ? "Create a password" : "Enter your password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert>
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSignUp ? "Sign Up" : "Sign In"}
            </Button>
          </form>

          {!isSignUp && (
            <div className="text-center mt-2">
              <Link href="/auth/reset-password" className="text-sm text-primary hover:underline">
                Forgot your password?
              </Link>
            </div>
          )}

          {!isSignUp && (
            <div className="mt-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>

              <GoogleSignInButton
                onClick={() => handleGoogleSignIn(userType)}
                isLoading={isLoading}
                className="mt-4"
              />

              {isAppleSignInEnabled() && (
                <AppleSignInButton
                  onClick={() => handleAppleSignIn(userType)}
                  isLoading={isLoading}
                  className="mt-2"
                />
              )}

              <PasskeySignInButton
                onClick={() => handlePasskeySignIn(userType)}
                isLoading={isLoading}
                className="mt-2"
              />

              <Button
                type="button"
                variant="outline"
                className="w-full mt-2"
                onClick={handleMagicLink}
                disabled={isLoading}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send Magic Link
              </Button>
            </div>
          )}

          <div className="mt-4 text-center text-sm">
            <p className="text-muted-foreground">
              {isSignUp ? (
                <>
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => setIsSignUp(false)}
                    className="text-primary hover:underline"
                  >
                    Sign in
                  </button>
                </>
              ) : (
                <>
                  Don&apos;t have an account?{" "}
                  <button
                    type="button"
                    onClick={() => setIsSignUp(true)}
                    className="text-primary hover:underline"
                  >
                    Sign up
                  </button>
                </>
              )}
            </p>
            <p className="text-muted-foreground mt-2">{helpText}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
